import { ApiGalleryItem, ApiItemStatus, ApiOverlayGallery, FetchVideoDataResponse } from '../common/requestsTypings';
import { ResourceType } from '../common/typings';
import { CreativeOptionSourceType } from '../CreativeOptions/typings';
import { GalleryCollectionItem, GalleryPaneTypes } from '../GalleryPane/typings';

import {
    FileEditorOptions,
    FileEditorsState,
    FileEditorState,
    FileEditorStatus,
    PreviewResource,
    PreviewResourceType
} from './typings';

export function getInitialFileEditorStatus(fileId: string | null): FileEditorStatus {
    if (fileId) {
        return FileEditorStatus.EDIT;
    } else {
        return FileEditorStatus.NEW;
    }
}

export function getPreviewResource(item: ApiGalleryItem | GalleryCollectionItem | ApiOverlayGallery): PreviewResource {
    switch (item.type) {
        case ResourceType.video:
        case ResourceType.image:
        case undefined:
            return {
                type: PreviewResourceType.IMAGE,
                previewUrl: item.thumbnailUrl
            };

        case ResourceType.audio:
            return {
                type: PreviewResourceType.AUDIO,
                name: item.name,
                duration: item.duration
            };

        case ResourceType.overlay:
            return {
                type: PreviewResourceType.OVERLAY,
                name: item.name
            };

        default:
            throw 'Resource type is not supported!';
    }
}

export function isFromGallery(item: ApiGalleryItem | GalleryCollectionItem | ApiOverlayGallery): boolean {
    switch (item.type) {
        case ResourceType.video:
        case ResourceType.image:
        case ResourceType.audio:
            return Boolean(item.stock_file_id);
        case ResourceType.overlay:
            return false;

        case undefined:
            return Boolean(item.stockFileId);

        default:
            throw 'Item type is not supported!';
    }
}

export function getFileEditorEnabledGalleryTypes(sources: CreativeOptionSourceType[]): GalleryPaneTypes[] {
    return sources.reduce((types: GalleryPaneTypes[], source: CreativeOptionSourceType) => {
        switch (source) {
            case CreativeOptionSourceType.stock:
                types.push(GalleryPaneTypes.stock);
                break;

            case CreativeOptionSourceType.recent:
                types.push(GalleryPaneTypes.recent);
                break;

            case CreativeOptionSourceType.idea:
                types.push(GalleryPaneTypes.idea);
                break;

            default:
                break;
        }

        return types;
    }, []);
}

// если у какого-либо загрузчика есть fileId, но нет превью, значит еще не подгрузился
export function fileEditorsLoaded(state: FileEditorsState): boolean {
    return !Object.keys(state).some(editorId => Boolean(state[editorId].fileId) && !state[editorId].preview);
}

export function shouldUpdatePreview(state: FileEditorsState, editorId: string, newPreviewUrl: string): boolean {
    const preview = state[editorId].preview;

    if (!preview) {
        return true;
    }

    if (preview.type !== 'image') {
        return false;
    }

    return newPreviewUrl !== preview.previewUrl;
}

export function fileEditorsLongUpload(state: FileEditorsState, skipId?: string): boolean {
    return Object.keys(state).some(
        editorId => Boolean(state[editorId].longUpload) && (skipId ? editorId !== skipId : true)
    );
}

export function fileEditorsUploading(
    state: FileEditorsState,
    skipId?: string,
    skipLongUpload?: boolean
): boolean | undefined {
    return Object.keys(state).some(
        editorId =>
            Boolean(state[editorId].upload) &&
            (skipId ? editorId !== skipId : true) &&
            (skipLongUpload ? !state[editorId].longUpload : true)
    );
}

export function getAudioFileEditorId(state: FileEditorsState): string | undefined {
    const audioEditorId = Object.keys(state).find((id: string) => {
        const editor = state[id];

        return editor.options ? editor.options.resourceType === ResourceType.audio : false;
    });

    if (!audioEditorId) {
        return undefined;
    }

    return audioEditorId;
}

export function isVideoConverting(response: FetchVideoDataResponse): boolean {
    return (
        response.type === ResourceType.video &&
        (response.status === ApiItemStatus.converting || response.status === ApiItemStatus.new)
    );
}

export function fileEditorsWithResourceType(
    state: FileEditorsState,
    resourceType: ResourceType,
    skipId: string
): string[] {
    return Object.keys(state).filter(editorId => {
        if (editorId === skipId) {
            return false;
        }

        const editor: FileEditorState = state[editorId];
        if (!editor) {
            return false;
        }

        const options: FileEditorOptions | undefined = editor.options;
        if (options) {
            return options.resourceType === resourceType;
        }

        return false;
    });
}
